/**
 * /subtitle-editor (SEO_BRIEF_04 item 13): open a file, edit it, save it. All in the browser.
 *
 * The page is the frame around lib/cue-editor.ts: the drop zone, the file line, the encoding and
 * frame-rate prompts, and the save. The worker reads the file (`parse`), holds the document and its
 * undo history (`editor`), and writes it (`write`); this module holds a `DocumentId` and nothing
 * larger, as every tool page does.
 *
 * A video or audio file dropped while a subtitle file is open goes to the preview player instead:
 * it plays from an object URL beside the list and is never read, parsed or sent. A video dropped
 * first is read for its embedded subtitles, as on every tool page, and then plays as well.
 */

import type { Framerate, WriteFormat } from '../../../../../contracts/cue-model.ts';
import type { ParsedInfo, WritePayload } from '../../../../../src/core/protocol.ts';
import { labelled } from '../../../../../src/tools/ops-from-values.ts';
import { type CueEditorHandle, type EditorState, mountCueEditor } from '../lib/cue-editor.ts';
import { defaultSaveFormat } from '../lib/cue-editor-model.ts';
import { must, on, qs } from '../lib/dom.ts';
import { saveBytes } from '../lib/download.ts';
import { classify, extensionOf, mountDropzone, videoTypeName } from '../lib/dropzone.ts';
import { track } from '../lib/events.ts';
import { shortFilename } from '../lib/format.ts';
import { parseFramerate } from '../lib/option-panel.ts';
import { showState } from '../lib/states.ts';
import { failedFormat, usageCounter } from '../lib/usage.ts';
import { downloadWriteOptions } from '../lib/write-options.ts';
import { isVersionMismatch, WorkerFailure, worker } from '../lib/worker.ts';

interface EditorPageConfig {
  readonly tool: { readonly slug: string; readonly label: string };
  readonly read_formats: readonly string[];
  readonly write_formats: readonly WriteFormat[];
  /** The format "Save as" starts on: SRT on /srt-editor, VTT on /vtt-editor, null elsewhere. */
  readonly to_format?: string | null;
}

function readConfig(): EditorPageConfig | null {
  const node = qs<HTMLScriptElement>('script[data-tool-config]');
  if (!node?.textContent) return null;
  try {
    const parsed = JSON.parse(node.textContent) as Partial<EditorPageConfig>;
    if (!parsed.tool || !Array.isArray(parsed.write_formats)) return null;
    return parsed as EditorPageConfig;
  } catch {
    return null;
  }
}

/** The worker asked for a frame rate: a MicroDVD .sub read, or a .sub write. */
function asksForRate(err: unknown): boolean {
  return (
    err instanceof WorkerFailure &&
    (err.reason === 'needs-framerate' || err.reason === 'framerate-required')
  );
}

/** How long after the last edit the save is prepared, so Download hands it over at once. */
const PREPARE_AFTER_MS = 600;

export function mountEditorPage(): void {
  const page = qs<HTMLElement>('[data-editor-page]');
  const found = readConfig();
  if (!page || !found) return;
  const config: EditorPageConfig = found;

  const region = must<HTMLElement>('#banner-region');
  const dropHost = must<HTMLElement>('[data-dropzone-host]');
  const editorRoot = must<HTMLElement>('[data-cue-editor]');
  const fileBox = qs<HTMLElement>('[data-editor-file]');
  const formatField = qs<HTMLElement>('[data-write-format-field]');
  const formatSelect = qs<HTMLSelectElement>('[data-write-format-select]');
  const downloadButton = qs<HTMLButtonElement>('[data-download]');
  const fpsField = qs<HTMLElement>('[data-fps-field]');
  const fpsInput = qs<HTMLInputElement>('[data-fps-input]');
  const encodingSelect = qs<HTMLSelectElement>('[data-encoding-override]');
  const encodingPanel = qs<HTMLDetailsElement>('[data-encoding-panel]');
  const encodingHint = qs<HTMLElement>('[data-encoding-hint]');
  const encodingLocked = qs<HTMLElement>('[data-encoding-locked]');
  const downloadWord = downloadButton?.textContent?.trim() ?? '';

  const client = worker();
  const usage = usageCounter(config.tool.slug);

  let editor: CueEditorHandle | null = null;
  let opened: { readonly file: File; readonly info: ParsedInfo } | null = null;
  let generation = 0;
  /** What the frame-rate box is answering: a read that needs one, or a .sub save. */
  let fpsFor: 'read' | 'write' | null = null;
  let writeRate: Framerate | null = null;
  let prepared: {
    readonly version: number;
    readonly format: WriteFormat;
    readonly payload: WritePayload;
  } | null = null;
  let prepareTimer = 0;

  function message(key: string): string {
    return qs<HTMLElement>(`[data-editor-label="${key}"]`, editorRoot)?.textContent?.trim() ?? '';
  }

  function notice(text: string): void {
    const p = document.createElement('p');
    p.className = 'banner banner--info';
    p.textContent = text;
    region.replaceChildren(p);
  }

  function handleWorkerError(err: unknown): void {
    if (isVersionMismatch(err)) {
      console.error('[editor]', err);
      showState(region, 'STALE_PAGE', {});
      return;
    }
    if (err instanceof WorkerFailure) {
      if (asksForRate(err)) {
        fpsFor = opened === null || err.reason === 'needs-framerate' ? 'read' : 'write';
        if (fpsField) fpsField.hidden = false;
        fpsInput?.focus();
        return;
      }
      const reason = err.reason;
      if (reason === 'no-subtitle-track') {
        // A video with no subtitles inside: it is the preview, not the file to edit.
        notice(message('open-first'));
        return;
      }
      if (reason === 'is-archive') {
        showState(region, 'B5_ARCHIVE_HERE', {});
        return;
      }
      if (reason === 'unsupported-binary' || reason === 'unknown-format') {
        showState(region, 'B5_UNSUPPORTED_BINARY', {});
        return;
      }
      if (reason === 'empty' || reason === 'no-cues') {
        showState(region, 'B5_EMPTY', {});
        return;
      }
      const detail = err.detail as { line?: number; message?: string } | null;
      showState(region, 'B5_PARSE_FAILED', {
        format: extensionOf(pending?.name ?? '').toUpperCase(),
        line: detail?.line === undefined ? '' : String(detail.line),
        detail: detail?.message ?? reason,
      });
      return;
    }
    console.error('[editor]', err);
    showState(region, 'NETWORK', {});
  }

  /* ── open ──────────────────────────────────────────────────────────────── */

  /** The file being read, for the error lines. */
  let pending: File | null = null;

  function close(): void {
    clearTimeout(prepareTimer);
    prepared = null;
    editor?.destroy();
    editor = null;
    if (opened) client.dispose([opened.info.docId], [opened.info.inputId]);
    opened = null;
    if (downloadButton) downloadButton.disabled = true;
  }

  function paintFile(info: ParsedInfo, file: File): void {
    if (!fileBox) return;
    fileBox.hidden = false;
    const set = (name: string, text: string) => {
      const node = qs<HTMLElement>(`[data-slot="${name}"]`, fileBox);
      if (node) node.textContent = text;
    };
    set('filename', shortFilename(file.name));
    set('format', info.format.toUpperCase());
    set('cue_count', String(info.cueCount));
    set('encoding', info.encoding.label);
    if (encodingSelect) {
      encodingSelect.value = info.encoding.label;
      encodingSelect.disabled = false;
    }
    if (encodingPanel) encodingPanel.open = info.needsEncodingOverride;
    if (encodingHint) encodingHint.hidden = false;
    if (encodingLocked) encodingLocked.hidden = true;
  }

  function onEdit(state: EditorState): void {
    // Re-reading the file would throw the edits away, so it locks until they are undone.
    const locked = state.undoDepth > 0;
    if (encodingSelect) encodingSelect.disabled = locked;
    if (encodingHint) encodingHint.hidden = locked;
    if (encodingLocked) encodingLocked.hidden = !locked;
    const count = fileBox ? qs<HTMLElement>('[data-slot="cue_count"]', fileBox) : null;
    if (count) count.textContent = String(state.cueCount);
    schedulePrepare();
  }

  async function open(
    file: File,
    over: { readonly framerate?: Framerate; readonly encoding?: string } = {},
  ): Promise<void> {
    const mine = ++generation;
    const kind = classify(file);
    pending = file;
    region.replaceChildren();
    // A new file supersedes the last one BEFORE it is read, as on every tool page: a .sub
    // waiting for its frame rate must not leave the previous file editable and downloadable
    // under the question about this one.
    close();
    if (fileBox) fileBox.hidden = true;
    if (formatField) formatField.hidden = true;
    if (kind === 'container') drop.setLoaderStatus('Loading the video reader… this happens once.');
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const info = await client.request('parse', {
        inputName: file.name,
        bytes,
        retainRaw: false,
        retainBytes: false,
        ...(over.framerate === undefined ? {} : { framerate: over.framerate }),
        ...(over.encoding === undefined ? {} : { encodingOverride: over.encoding }),
      });
      if (mine !== generation) {
        client.dispose([info.docId], [info.inputId]);
        return;
      }
      close();
      opened = { file, info };
      if (fpsField && over.framerate === undefined) fpsField.hidden = true;
      fpsFor = null;
      writeRate = info.framerate;
      paintFile(info, file);
      editor = mountCueEditor(editorRoot, {
        client,
        docId: info.docId,
        cueCount: info.cueCount,
        onChange: onEdit,
        onError: handleWorkerError,
      });
      if (kind === 'container') editor.setVideo(file);
      const format = formatSelect
        ? defaultSaveFormat(info.format, config.write_formats, config.to_format)
        : null;
      if (formatSelect && format) formatSelect.value = format;
      if (formatField) formatField.hidden = false;
      paintDownload();
      usage.loaded(file, info.format);
      track('tool_used', { tool: config.tool.slug });
      schedulePrepare();
    } catch (err) {
      if (mine !== generation) return;
      if (!asksForRate(err)) usage.failed(file, failedFormat(extensionOf(file.name), err));
      handleWorkerError(err);
    } finally {
      if (mine === generation) drop.setLoaderStatus(null);
    }
  }

  function take(file: File | undefined): void {
    if (!file) return;
    const kind = classify(file);
    if (kind === 'audio' || (kind === 'container' && editor !== null)) {
      // Media for the player, never for the reader.
      if (editor) {
        region.replaceChildren();
        editor.setVideo(file);
      } else {
        notice(message('open-first'));
      }
      return;
    }
    if (kind === 'other-video') {
      // A video for the player that it can't play (WMV, FLV, MPG…), counted as `video`.
      usage.failed(file, 'video');
      showState(region, 'B5_VIDEO_FORMAT', { format: videoTypeName(file.name) });
      return;
    }
    if (kind === 'archive') {
      usage.failed(file, 'zip');
      showState(region, 'B5_ARCHIVE_HERE', {});
      return;
    }
    if (kind === 'unknown') {
      usage.failed(file, failedFormat(extensionOf(file.name), null));
      showState(region, 'B5_UNSUPPORTED_BINARY', {});
      return;
    }
    if (fpsInput) fpsInput.value = '';
    void open(file);
  }

  const drop = mountDropzone(dropHost, {
    onFiles: (files) => take(files[0]),
  });

  /* ── save ──────────────────────────────────────────────────────────────── */

  function chosenFormat(): WriteFormat | null {
    const value = formatSelect?.value ?? config.write_formats[0];
    return config.write_formats.find((f) => f === value) ?? null;
  }

  function paintDownload(): void {
    if (!downloadButton) return;
    const format = chosenFormat();
    downloadButton.textContent = format ? `${downloadWord} ${format.toUpperCase()}` : downloadWord;
    downloadButton.disabled = editor === null || format === null;
  }

  async function produce(format: WriteFormat): Promise<WritePayload | null> {
    if (!editor) return null;
    const current = editor;
    await current.flush();
    const version = current.state().version;
    const payload = await current.write(
      format,
      downloadWriteOptions(format, format === 'sub' && writeRate ? { framerate: writeRate } : {}),
    );
    if (editor !== current) return null;
    if (current.state().version === version) prepared = { version, format, payload };
    return payload;
  }

  function schedulePrepare(): void {
    clearTimeout(prepareTimer);
    prepared = null;
    prepareTimer = window.setTimeout(() => {
      const format = chosenFormat();
      // A .sub with no rate yet is asked for when Download is pressed, not behind the scenes.
      if (!format || (format === 'sub' && writeRate === null)) return;
      void produce(format).catch(() => undefined);
    }, PREPARE_AFTER_MS);
  }

  function handOver(format: WriteFormat, payload: WritePayload): void {
    saveBytes(payload.output, payload.filename, format, 'utf-8');
    usage.downloaded(format, 'utf-8');
    track('format_download', { format });
  }

  if (downloadButton) {
    on(downloadButton, 'click', () => {
      const format = chosenFormat();
      if (!editor || !format) return;
      const fresh = prepared;
      if (fresh && fresh.format === format && fresh.version === editor.state().version) {
        // Synchronous from the click to the save: the bytes were made while the page was idle.
        handOver(format, fresh.payload);
        return;
      }
      region.replaceChildren();
      void produce(format)
        .then((payload) => {
          if (payload) handOver(format, payload);
        })
        .catch(handleWorkerError);
    });
  }

  if (formatSelect) {
    on(formatSelect, 'change', () => {
      // A rate asked for a .sub save means nothing once another format is chosen.
      if (fpsFor === 'write' && chosenFormat() !== 'sub' && fpsField) {
        fpsField.hidden = true;
        fpsFor = null;
      }
      paintDownload();
      schedulePrepare();
    });
  }

  /* ── the re-reads: encoding and frame rate ─────────────────────────────── */

  if (encodingSelect) {
    on(encodingSelect, 'change', () => {
      if (!opened || (editor && editor.state().undoDepth > 0)) return;
      void open(opened.file, { encoding: encodingSelect.value });
    });
  }

  if (fpsInput) {
    let lastAsked = '';
    const answer = () => {
      const typed = fpsInput.value.trim();
      const rate = labelled(parseFramerate(typed));
      fpsInput.setAttribute('aria-invalid', rate === null && typed !== '' ? 'true' : 'false');
      if (rate === null) return;
      const asked = `${fpsFor}:${rate.num}/${rate.den}`;
      if (asked === lastAsked) return;
      lastAsked = asked;
      region.replaceChildren();
      if (fpsFor === 'write') {
        writeRate = rate;
        if (fpsField) fpsField.hidden = true;
        schedulePrepare();
        return;
      }
      if (pending) void open(pending, { framerate: rate });
    };
    on(fpsInput, 'change', answer);
    on(fpsInput, 'keydown', (ev) => {
      if ((ev as KeyboardEvent).key !== 'Enter') return;
      ev.preventDefault();
      answer();
    });
    on(fpsInput, 'input', () => {
      lastAsked = '';
    });
  }

  on(region, 'click', (ev) => {
    const target = ev.target;
    if (target instanceof HTMLElement && target.getAttribute('data-action') === 'reload') {
      location.reload();
    }
  });

  addEventListener('pagehide', () => {
    close();
    drop.destroy();
  });
}
